/**
 * LaunchReadinessPanel — admin Control Room → Launch tab.
 *
 * A single, read-only go/no-go dashboard that aggregates launch-critical
 * signals from GET /api/admin/launch-readiness. Auto-derived technical
 * checks (build, DB, maps, Twilio SMS/voice/booking/WhatsApp, launch-reset
 * safety) plus the operational items that need a manual sign-off
 * (GitHub backup, dispatch flow, data cleanup).
 *
 * Purely informational — no destructive actions here.
 */
import React, { useEffect, useState } from "react";
import { Rocket, RefreshCw, CheckCircle2, AlertTriangle, Clock, XCircle, FlaskConical, Copy, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  ready:   { label: "Ready",        cls: "text-[#065F46] bg-[#ECFDF5] border-[#10B981]", Icon: CheckCircle2 },
  verify:  { label: "Verify",       cls: "text-[#92400E] bg-[#FFFBEB] border-[#F59E0B]", Icon: Clock },
  waiting: { label: "Waiting",      cls: "text-[#1E3A8A] bg-[#EFF6FF] border-[#3B82F6]", Icon: Clock },
  blocked: { label: "Blocked",      cls: "text-[#7F1D1D] bg-[#FEF2F2] border-[#EF4444]", Icon: XCircle },
};

const OVERALL_META = {
  ready:   { label: "ALL SYSTEMS GO", cls: "border-[#10B981] bg-[#ECFDF5] text-[#065F46]", Icon: CheckCircle2 },
  verify:  { label: "MANUAL CHECKS PENDING", cls: "border-[#F59E0B] bg-[#FFFBEB] text-[#92400E]", Icon: AlertTriangle },
  blocked: { label: "LAUNCH BLOCKED", cls: "border-[#EF4444] bg-[#FEF2F2] text-[#7F1D1D]", Icon: XCircle },
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.verify;
  const { Icon } = m;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-bold uppercase tracking-wide border ${m.cls}`}>
      <Icon className="w-3.5 h-3.5" /> {m.label}
    </span>
  );
};

export const LaunchReadinessPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/launch-readiness");
      setData(data);
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const overall = data ? (OVERALL_META[data.overall] || OVERALL_META.verify) : null;

  // ── Production cleanup DRY-RUN (read-only, non-destructive) ──────────
  const [dryRunning, setDryRunning] = useState(false);
  const [dryReport, setDryReport] = useState(null);
  const [dryError, setDryError] = useState(null);

  const runDryRun = async () => {
    setDryRunning(true);
    setDryError(null);
    setDryReport(null);
    try {
      const { data } = await api.post("/admin/launch-reset/dry-run");
      setDryReport(data);
      toast.success("Dry-run complete — nothing was deleted.");
    } catch (e) {
      // Surface the structured backend reason (kill-switch / keeper / token).
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "launch_reset_disabled") {
        setDryError("Launch reset is DISABLED. Set ALLOW_LAUNCH_RESET=true in the environment, redeploy, then retry.");
      } else if (code === "keeper_email_not_found") {
        setDryError(`Keeper email(s) not found in this database: ${(detail.missing || []).join(", ")}`);
      } else {
        setDryError(formatApiError(e));
      }
    } finally {
      setDryRunning(false);
    }
  };

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(dryReport, null, 2));
      toast.success("Dry-run JSON copied to clipboard.");
    } catch {
      toast.error("Could not copy — select the text manually.");
    }
  };

  return (
    <div className="bg-white" data-testid="launch-readiness-panel">
      <div className="px-4 py-4 border-b border-border flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-[#002FA7]" />
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight">Launch Readiness</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              One-page go/no-go checklist. Auto-verified signals plus the operational items that need your sign-off.
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="text-xs px-3 py-2 border border-border hover:bg-secondary transition-colors flex items-center gap-1.5 disabled:opacity-60"
          data-testid="readiness-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="p-4 max-w-3xl mx-auto space-y-4">
        {err && (
          <div className="border-2 border-destructive bg-destructive/5 p-3 text-sm text-destructive" data-testid="readiness-error">
            {err}
          </div>
        )}

        {data && overall && (
          <div className={`border-2 p-4 flex items-center gap-3 ${overall.cls}`} data-testid="readiness-overall" data-overall={data.overall}>
            <overall.Icon className="w-6 h-6 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-heading font-black text-lg">{overall.label}</div>
              <div className="text-xs mt-0.5">
                {data.counts.ready} ready · {data.counts.verify} to verify · {data.counts.waiting} waiting · {data.counts.blocked} blocked
              </div>
            </div>
          </div>
        )}

        {data && (
          <div className="border border-border divide-y divide-border" data-testid="readiness-list">
            {data.items.map((it) => (
              <div key={it.key} className="flex items-start justify-between gap-3 px-3 py-3" data-testid={`readiness-item-${it.key}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{it.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{it.detail}</div>
                </div>
                <div className="flex-shrink-0" data-testid={`readiness-status-${it.key}`}>
                  <StatusPill status={it.status} />
                </div>
              </div>
            ))}
          </div>
        )}

        {data && (
          <p className="text-[11px] text-muted-foreground">
            Generated {new Date(data.generated_at).toLocaleString("en-GB")}. "Verify" items are manual operational
            sign-offs; "Blocked" items are hard stops detected automatically.
          </p>
        )}

        {/* ── Production cleanup dry-run (read-only) ───────────────── */}
        <div className="border-2 border-[#002FA7] mt-6" data-testid="dry-run-section">
          <div className="px-4 py-3 bg-[#002FA7] text-white flex items-center gap-2">
            <FlaskConical className="w-4 h-4" />
            <div className="flex-1">
              <div className="font-heading font-bold text-sm">Production Cleanup — Dry-Run</div>
              <div className="text-[11px] opacity-90">Read-only preview. Keeps the 6 genuine accounts. Deletes nothing.</div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">
              Requires <code className="font-mono">ALLOW_LAUNCH_RESET=true</code> in the environment. This shows exactly
              what a cleanup <em>would</em> remove and confirms the 6 keeper accounts are preserved. There is no
              destructive button here by design.
            </p>
            <button
              onClick={runDryRun}
              disabled={dryRunning}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002685] transition-colors disabled:opacity-60"
              data-testid="run-dry-run-btn"
            >
              <FlaskConical className={`w-4 h-4 ${dryRunning ? "animate-pulse" : ""}`} />
              {dryRunning ? "Running dry-run…" : "Run Cleanup Dry-Run"}
            </button>

            {dryError && (
              <div className="border-2 border-destructive bg-destructive/5 p-3 text-sm text-destructive" data-testid="dry-run-error">
                {dryError}
              </div>
            )}

            {dryReport && (
              <div className="space-y-2" data-testid="dry-run-report">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-bold border text-[#065F46] bg-[#ECFDF5] border-[#10B981]">
                    <CheckCircle2 className="w-3.5 h-3.5" /> DRY-RUN — nothing deleted
                  </span>
                  {Array.isArray(dryReport.would_keep_users) && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold border border-border" data-testid="dry-run-keep-count">
                      Keeping {dryReport.would_keep_users.length} accounts
                    </span>
                  )}
                  {typeof dryReport.would_delete_user_count === "number" && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-semibold border border-border text-[#92400E]" data-testid="dry-run-delete-count">
                      <Trash2 className="w-3.5 h-3.5" /> Would delete {dryReport.would_delete_user_count} users
                    </span>
                  )}
                  <button
                    onClick={copyReport}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border border-border hover:bg-secondary transition-colors ml-auto"
                    data-testid="dry-run-copy-btn"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy JSON
                  </button>
                </div>
                <pre
                  className="text-[11px] leading-relaxed bg-[#0A0A0A] text-[#E5E7EB] p-3 overflow-auto max-h-96 font-mono"
                  data-testid="dry-run-json"
                >
                  {JSON.stringify(dryReport, null, 2)}
                </pre>
                <p className="text-[11px] text-muted-foreground">
                  Copy this JSON and share it for review. Afterwards, set <code className="font-mono">ALLOW_LAUNCH_RESET=false</code> again.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaunchReadinessPanel;
